import { getVapidPublicKey, subscribePush, unsubscribePush } from '../api';

/**
 * Client-side web push helpers: subscribe/unsubscribe this browser and
 * report current status. Push requires the service worker (production
 * builds on HTTPS) and user permission.
 */

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Current subscription for this browser, or null. */
export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Enable push on this device: request permission, subscribe with the server's
 * VAPID key, and register the subscription server-side.
 * Throws Error with a user-facing message on failure.
 */
export async function enablePush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was denied — enable it in your browser settings');
  }
  const keyRes = await getVapidPublicKey();
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey),
  });
  await subscribePush(subscription.toJSON());
  return subscription;
}

/** Disable push on this device (unsubscribes locally and server-side). */
export async function disablePush() {
  const subscription = await getExistingSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  try {
    await subscription.unsubscribe();
  } catch {
    /* browser-side unsubscribe can fail after SW updates; server row still removed */
  }
  await unsubscribePush(endpoint);
}
